<template lang="pug">
fieldset-textarea(
  ref="field"
  flex
  large
  wide
  use-placeholder
  :name="`section_${sectionIndex}`"
  :label="sectionIndex === 0 ? 'Write your post' : ''"
  v-model="sectionContent"
  v-on="inputListeners"
)
</template>

<script>
import FieldsetTextarea from '@/components/fieldset-textarea'

export default {
  name: 'post-form__section--default',
  components: {
    FieldsetTextarea,
  },
  props: {
    section: {
      type: Object,
      required: true,
    },
    sectionIndex: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      internalValue: '',
    }
  },
  watch: {
    section: {
      deep: true,
      handler(val, old) {
        const noChange = val && old && val.content === old.content
        const noContent = !val || !val.content
        if (noChange) return
        if (noContent) {
          this.internalValue = ''
          return
        }
        this.internalValue = val.content
      },
    },
  },
  computed: {
    sectionContent: {
      get() {
        return this.internalValue
      },
      set(val) {
        this.internalValue = val
        this.$emit('change', { index: this.sectionIndex, content: val })
      },
    },
    inputListeners() {
      const vm = this
      return Object.assign({}, this.$listeners, {
        keyup(e) {
          vm.onKeyup(e)
          vm.$emit('keyup', e)
        },
        change(e) {
          vm.$emit('change', { 
            index: vm.sectionIndex, 
            content: e
          })
        }
      })
    }
  },
  mounted() {
    this.internalValue = this.section.content
  },
  methods: {
    // TODO: properly expose these methods upward
    resize() {
      this.$refs.field.resize()
      return this
    },
    focus() {
      this.$refs.field.focus()
      return this
    },
    error(msg) {
      this.$refs.field.error(msg)
    },

    onKeyup({ altKey, code, shiftKey, target: { selectionStart, selectionEnd } }) {
      const cursorAtStart = selectionStart === 0
      // eslint-disable-next-line default-case
      switch (code) {
        case 'Enter':
          return this.onEnter({ selectionEnd, shiftKey })
        case 'Backspace':
          return cursorAtStart && this.mergeIntoPrevSection()
        case 'ArrowUp':
          return altKey && this.prevSection()
        case 'ArrowDown':
          return altKey && this.nextSection()
      }
    },
    onEnter({ shiftKey, selectionEnd }) {
      if (shiftKey) return // allow for new lines when shift is down

      const cursorAtEnd = selectionEnd === this.sectionContent.length
      if (cursorAtEnd) {
        this.sectionContent = this.sectionContent.replace(/\n+$/, '') // remove trailing new lines
        return this.insertAfter()
      }
      
      const contentAfterCursor = this.sectionContent
        .substring(selectionEnd)
        .replace(/^\n+/, '')
      this.sectionContent = this.sectionContent
        .substring(0, selectionEnd)
        .replace(/\n+$/, '')
      this.insertAfter(contentAfterCursor)
    },
    mergeIntoPrevSection() {
      this.$emit('prevSection', {
        index: this.sectionIndex,
        content: this.sectionContent,
        moveContent: true,
      })
      return this
    },
    insertAfter(content = false) {
      if (content) {
        this.$emit('newSection', {
          after: this.sectionIndex,
          content,
        })
      } else {
        this.$emit('nextSection', {
          index: this.sectionIndex,
          createSection: true,
        })
      }
      return this
    },
    prevSection() {
      this.$emit('prevSection', { index: this.sectionIndex })
      return this
    },
    nextSection() {
      this.$emit('nextSection', { index: this.sectionIndex })
      return this
    },
  },
}
</script>

<style lang="sass">
.post-form__section
  --input-fontsize: var(--post-content-font-size)
  --input-lineheight: var(--post-content-line-height)

  .fieldset.--large
    --input-fontsize: var(--post-content-font-size)
    --input-lineheight: var(--post-content-line-height)

  .fieldset__input
    border-bottom: none
    background-position: center left
    background-size: var(--underline-effect-line-height) 0%
    transition: background-size 0.3s, background-image 0.3s, border-color 0.3s, height 0.3s

    &:hover,
    &:active,
    &:focus
      border-bottom: none
      background-size: var(--underline-effect-line-height) calc(100% - var(--input-padding-v) * 2)
</style>
